import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { EmployeesApiService } from "src/services/employees/employees-api.service";
import { Props, State, EmployeeOptionValue, EmployeeQuickFilter } from "./interfaces";
import { useAppSelector } from "../../../custom-hooks/redux/redux";
import useAuthorization, { isAuthorized } from "../../../custom-hooks/useAuthorization/useAuthorization";
import { EmployeeRef } from "../../../models/employees";
import { EmployeesFilter } from "../../../models/employees/employees-filter";
import { QuickFilterOption } from "../../../reusable-components/QuickFilter/interfaces";
import QuickFilter from "../../../reusable-components/QuickFilter/QuickFilter";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";

export const EmployeesQuickfilter = <Q extends EmployeeQuickFilter>({
  quickFilters,
  handleChangeQuickFilters,
  withOrWithoutEmployeeOptions,
  allEmployeesOption,
  isMultiSelect,
  allEmployees,
  authorizations,
  optionIsAuthorizedChecker,
  optionIncluded,
}: Props<Q>) => {
  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const { t } = useTranslation();
  const tRP = "components.quickfilters.employees";

  const [employees, setCurrEmployees] = useState<EmployeeRef[]>([]);

  useEffect(() => {
    const includedEmployees = optionIncluded ? allEmployees.filter((emp) => optionIncluded(emp)) : allEmployees;

    setCurrEmployees(includedEmployees);

    const newOptions: QuickFilterOption<EmployeeOptionValue>[] = includedEmployees.map((emp) => ({
      name: `${emp.name ? `${emp.name}` : `${emp.firstName} ${emp.lastName}`}${
        emp.legalEntity ? ` (${emp.legalEntity.name})` : ""
      }`,
      value: { employeeIds: [emp.id], withoutEmployee: false },
      code: emp.employeeCode,
    }));

    setOptions((state) => ({ ...state, computedOptions: newOptions }));
  }, [allEmployees, optionIncluded]);

  const [loadEmployees, setLoadEmployees] = useState<boolean>(false);

  const canProcessDocumentNotLinkedToEmployee = useAuthorization([
    AUTHORIZATIONS.CompanyAccountsAccess.CanProcessDocumentNotLinkedToLe,
  ]);

  const [options, setOptions] = useState<State>({
    customOptions: [
      ...(allEmployeesOption
        ? [
            {
              name: t(`${tRP}.all_employees`),
              value: {
                employeeIds: optionIncluded
                  ? [...allEmployees.filter((emp) => optionIncluded(emp)).map((emp) => emp.id)]
                  : [],
                withoutEmployee: false,
              },
            },
          ]
        : []),
      ...(withOrWithoutEmployeeOptions
        ? [
            {
              name: t(`${tRP}.with_without_employee`),
              value: {
                withoutEmployee: false,
                employeeIds: optionIncluded
                  ? [...allEmployees.filter((emp) => optionIncluded(emp))].map((emp) => emp.id)
                  : [],
              },
            },
            ...(canProcessDocumentNotLinkedToEmployee
              ? [
                  {
                    name: t(`${tRP}.without_employee`),
                    value: {
                      withoutEmployee: true,
                      employeeIds: optionIncluded
                        ? [...allEmployees.filter((emp) => optionIncluded(emp))].map((emp) => emp.id)
                        : [],
                    },
                  },
                ]
              : []),
          ]
        : []),
    ],
    computedOptions: [],
  });

  useEffect(() => {
    if (employees) {
      const newOptions: QuickFilterOption<EmployeeOptionValue>[] = employees.map((emp) => ({
        name: `${emp.name ? `${emp.name}` : `${emp.firstName} ${emp.lastName}`}${
          emp.legalEntity ? ` (${emp.legalEntity.name})` : ""
        }`,
        value: { employeeIds: [emp.id], withoutEmployee: false },
        // code: emp.employeeCode,
      }));

      setOptions((state) => ({ ...state, computedOptions: newOptions }));
    }
  }, [employees]);

  const loadNewItems = () => {
    setLoadEmployees(true);
  };

  const loadItemCallback = useCallback(
    async (signal: AbortSignal) => {
      setLoadEmployees(true);
      try {
        const response = await new EmployeesApiService().loadItems(
          {
            ids: [],
            contextAuthorizations: authorizations ? [...authorizations] : [],
          } as Partial<EmployeesFilter>,
          undefined,
          undefined, // so that service API default sort is applied
          { pageNumber: 0, pageSize: 10000 },
          undefined,
          60,
          signal
        );
        if (!response.error) {
          const optionsIncluded = optionIncluded ? response.items.filter((emp) => optionIncluded(emp)) : response.items;

          const authorizedEmployees = authorizations
            ? optionIsAuthorizedChecker
              ? optionsIncluded.filter((emp) =>
                  optionIsAuthorizedChecker([...authorizations], currentUserAuthorizations, [emp.id])
                )
              : isAuthorized([...authorizations], currentUserAuthorizations)
              ? optionsIncluded
              : []
            : optionsIncluded;

          setCurrEmployees(authorizedEmployees as EmployeeRef[]);
        }
      } catch (error: any) {
        console.error(error);
      } finally {
        setLoadEmployees(false);
      }
    },
    [authorizations, optionIsAuthorizedChecker, optionIncluded, currentUserAuthorizations]
  );

  useEffect(() => {
    const abortController = new AbortController();

    loadEmployees && loadItemCallback(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [loadEmployees, loadItemCallback]);

  return (
    <QuickFilter
      loading={loadEmployees}
      loadNewItems={loadNewItems}
      allItems={employees}
      options={[...options.customOptions, ...options.computedOptions]}
      quickFilters={quickFilters}
      quickFilter="employees"
      handleChangeQuickFilter={handleChangeQuickFilters}
      shrinkedLabel={t(`${tRP}.shrinked_label`)}
      isMultiSelect={isMultiSelect}
      multiSelectField="employeeIds"
      customWidth={"200px"}
      maxMenuWidth="280px"
    />
  );
};
